/**
 * Manages promotion labels visibility and overflow indicators for vehicle cards
 * Uses IntersectionObserver for performance with multiple cards on page
 */
export default class PromotionLabelsManager {
  constructor() {
    this.containers = new Map();
    this.resizeTimeout = null;
    this.init();
  }

  init() {
    // Single observer for all containers - more efficient than individual observers
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.processContainer(entry.target);
          }
        });
      },
      {
        rootMargin: '50px', // Start processing slightly before visible
        threshold: 0.1,
      },
    );

    this.findAndObserveContainers();
    this.setupResizeHandler();
  }

  findAndObserveContainers() {
    this.cleanupStaleContainers();

    const containers = document.querySelectorAll('[data-promotion-container]');
    containers.forEach((container) => {
      // Only observe if not already being tracked
      if (!this.containers.has(container)) {
        this.observer.observe(container);
      }
    });
  }

  cleanupStaleContainers() {
    this.containers.forEach((elements, container) => {
      if (!document.contains(container)) {
        this.containers.delete(container);
        this.observer.unobserve(container);
      }
    });
  }

  processContainer(container) {
    // Only process once when in viewport for performance
    this.observer.unobserve(container);

    const content = container.querySelector('[data-promotion-content]');
    const additional = container.querySelector('[data-promotion-additional]');
    const chips = container.querySelectorAll('[data-promotion-chip]');

    if (!content || !additional || chips.length === 0) return;

    const originalAdditionalCount = parseInt(
      additional.querySelector('[data-additional-count]').textContent,
    ) || 0;

    this.containers.set(container, {
      content,
      additional,
      chips,
      originalAdditionalCount,
    });

    this.calculateVisibility(
      container,
      content,
      additional,
      chips,
      originalAdditionalCount,
    );
  }

  calculateVisibility(
    container,
    content,
    additional,
    chips,
    originalAdditionalCount,
  ) {
    const containerWidth = container.offsetWidth;
    // Approximate width of "+X more" indicator
    const additionalWidth = 70;
    const gradientStartPosition = containerWidth - additionalWidth;

    let visibleCount = 0;
    let overlappingCount = 0;

    // Use the original PHP additional count (don't read from DOM to avoid accumulation)
    const phpAdditionalCount = originalAdditionalCount || 0;
    const hasPhpAdditionalPromotions = phpAdditionalCount > 0;

    // Calculate how many chips can fit and detect overlaps
    for (let i = 0; i < chips.length; i++) {
      const chip = chips[i];
      const chipRect = chip.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Calculate chip position relative to container
      const chipRightEdge = chipRect.right - containerRect.left;
      const chipLeftEdge = chipRect.left - containerRect.left;

      // Check if chip is completely outside container
      if (chipLeftEdge >= containerWidth) {
        // Chip is completely hidden
        break;
      }

      // Chip is at least partially visible
      visibleCount += 1;

      // Check if chip overlaps with gradient area
      if (chipRightEdge > gradientStartPosition) {
        overlappingCount += 1;
      }
    }

    const hiddenChipsCount = chips.length - visibleCount;

    // For overlapping chips, we want to show "+1 more" to indicate there's content under the gradient
    // For completely hidden chips, we show the actual count of hidden chips
    let indicatorCount = hiddenChipsCount + phpAdditionalCount;

    // If there are overlapping chips but no hidden chips, show "+1 more" to indicate overlap
    if (
      overlappingCount > 0
      && hiddenChipsCount === 0
      && phpAdditionalCount === 0
    ) {
      indicatorCount = 1;
    }

    // Show additional indicator if:
    // 1. There are PHP additional promotions, OR
    // 2. There are overlapping chips, OR
    // 3. There are completely hidden chips
    if (
      hasPhpAdditionalPromotions
      || overlappingCount > 0
      || hiddenChipsCount > 0
    ) {
      this.showAdditionalIndicator(container, additional, indicatorCount);
    } else {
      this.hideAdditionalIndicator(additional);
    }

    this.updateChipVisibility(chips, visibleCount);
  }

  showAdditionalIndicator(container, additional, hiddenCount) {
    const countElement = additional.querySelector('[data-additional-count]');

    if (hiddenCount <= 0) {
      this.hideAdditionalIndicator(additional);
      return;
    }

    if (countElement) {
      countElement.textContent = hiddenCount;
    }

    additional.classList.add('vehicle-card__promotion-additional--visible');
  }

  hideAdditionalIndicator(additional) {
    additional.classList.remove('vehicle-card__promotion-additional--visible');
  }

  updateChipVisibility(chips, visibleCount) {
    chips.forEach((chip, index) => {
      const chipElement = chip;
      chipElement.style.display = index < visibleCount ? 'block' : 'none';
    });
  }

  setupResizeHandler() {
    window.addEventListener('resize', () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 250);
    });
  }

  handleResize() {
    this.cleanupStaleContainers();

    this.containers.forEach((elements, container) => {
      const rect = container.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (isVisible) {
        const {
          content, additional, chips, originalAdditionalCount,
        } = elements;
        this.calculateVisibility(
          container,
          content,
          additional,
          chips,
          originalAdditionalCount,
        );
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.promotionLabelsManager = new PromotionLabelsManager();
});
